import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardService, TeamSummaryData } from '../services/dashboard.service';
import { formatCompactCurrency } from '../utils/formatters';
import { Card, MetricCard } from '../components/Card';

// ============================================
// HELPER COMPONENTS
// ============================================

// Progress bar component
const ProgressBar: React.FC<{
  current: number;
  target: number;
  percentage: number;
  size?: 'small' | 'medium';
}> = ({ current, target, percentage, size = 'medium' }) => {
  const cappedPercentage = Math.min(percentage, 100);
  const isOnTrack = percentage >= 80;
  const isExceeding = percentage >= 100;

  return (
    <div>
      <div className={`w-full bg-gray-700 rounded-full overflow-hidden ${size === 'small' ? 'h-2' : 'h-3'}`}>
        <div
          className={`h-full rounded-full transition-all duration-500 ${
            isExceeding ? 'bg-green-500' : isOnTrack ? 'bg-blue-500' : 'bg-orange-500'
          }`}
          style={{ width: `${cappedPercentage}%` }}
        />
      </div>
      <div className="flex justify-between items-center mt-1">
        <span className="text-xs text-text-muted">
          {formatCompactCurrency(current)} / {formatCompactCurrency(target)}
        </span>
        <span className={`text-xs font-semibold ${
          isExceeding ? 'text-green-400' : isOnTrack ? 'text-blue-400' : 'text-orange-400'
        }`}>
          {percentage.toFixed(0)}%
        </span>
      </div>
    </div>
  );
};

// Mini confidence breakdown
const ConfidenceBreakdown: React.FC<{
  high: number;
  medium: number;
  low: number;
}> = ({ high, medium, low }) => {
  const total = high + medium + low;
  if (total === 0) return <span className="text-text-muted text-xs">No deals</span>;

  const highPct = (high / total) * 100;
  const medPct = (medium / total) * 100;
  const lowPct = (low / total) * 100;

  return (
    <div className="space-y-1">
      <div className="flex h-2 rounded-full overflow-hidden bg-gray-700">
        {highPct > 0 && (
          <div className="bg-green-500 h-full" style={{ width: `${highPct}%` }} />
        )}
        {medPct > 0 && (
          <div className="bg-yellow-500 h-full" style={{ width: `${medPct}%` }} />
        )}
        {lowPct > 0 && (
          <div className="bg-red-500 h-full" style={{ width: `${lowPct}%` }} />
        )}
      </div>
      <div className="flex justify-between text-xs">
        <span className="text-green-400">{formatCompactCurrency(high)}</span>
        <span className="text-yellow-400">{formatCompactCurrency(medium)}</span>
        <span className="text-red-400">{formatCompactCurrency(low)}</span>
      </div>
    </div>
  );
};

// Rep Card Component
const RepCard: React.FC<{
  rep: TeamSummaryData['teamMetrics'][0];
  onClick: () => void;
}> = ({ rep, onClick }) => {
  return (
    <Card>
      <div className="cursor-pointer" onClick={onClick}>
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="font-semibold text-primary text-lg">{rep.fullName}</h3>
            <p className="text-sm text-text-muted">{rep.role}</p>
          </div>
          <span
            className={`px-3 py-1 rounded text-sm font-medium ${
              rep.statusColor === 'success'
                ? 'bg-green-500/20 text-green-400'
                : rep.statusColor === 'warning'
                ? 'bg-yellow-500/20 text-yellow-400'
                : 'bg-red-500/20 text-red-400'
            }`}
          >
            {rep.targetProgress.toFixed(0)}%
          </span>
        </div>

        {/* Progress to Target */}
        <div className="mb-4">
          <div className="flex justify-between items-center mb-1">
            <span className="text-xs text-text-secondary font-medium">Progress to Target</span>
            <span className="text-xs text-text-muted">{rep.dealCounts.total} deals</span>
          </div>
          <ProgressBar
            current={rep.pipeline.total}
            target={rep.target}
            percentage={rep.targetProgress}
          />
        </div>

        {/* Pipeline Metrics */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="text-center">
            <p className="text-xs text-text-muted">Mar 2026</p>
            <p className="text-sm font-semibold text-primary">
              {formatCompactCurrency(rep.march2026?.total || 0)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-text-muted">FY26</p>
            <p className="text-sm font-semibold text-warning">
              {formatCompactCurrency(rep.fy26?.total || 0)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-text-muted">FY27</p>
            <p className="text-sm font-semibold text-success">
              {formatCompactCurrency(rep.fy27?.total || 0)}
            </p>
          </div>
        </div>

        {/* Confidence Breakdown */}
        <div className="mb-4">
          <p className="text-xs text-text-secondary font-medium mb-2">Pipeline by Confidence</p>
          <ConfidenceBreakdown
            high={rep.pipeline.high}
            medium={rep.pipeline.medium}
            low={rep.pipeline.low}
          />
        </div>

        {/* Top 3 Deals */}
        {rep.top3Deals && rep.top3Deals.length > 0 && (
          <div className="border-t border-border pt-3">
            <p className="text-xs text-text-secondary font-medium mb-2">Top Deals</p>
            <div className="space-y-2">
              {rep.top3Deals.map((deal, idx) => (
                <div key={deal.id} className="flex justify-between items-center">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs text-text-muted">#{idx + 1}</span>
                    <span className="text-sm text-text-primary truncate">{deal.dealName}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span
                      className={`text-xs px-1.5 py-0.5 rounded ${
                        deal.confidenceLevel === 'HIGH'
                          ? 'bg-green-500/20 text-green-400'
                          : deal.confidenceLevel === 'MEDIUM'
                          ? 'bg-yellow-500/20 text-yellow-400'
                          : 'bg-red-500/20 text-red-400'
                      }`}
                    >
                      {deal.confidenceLevel?.[0]}
                    </span>
                    <span className="text-sm font-medium text-text-primary">
                      {formatCompactCurrency(deal.weightedValue)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* View Details Link */}
        <div className="mt-4 pt-3 border-t border-border text-center">
          <span className="text-sm text-primary hover:text-blue-400 transition-colors">
            View Details →
          </span>
        </div>
      </div>
    </Card>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

export const TeamSummary: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<TeamSummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const teamData = await dashboardService.getTeamSummary();
        setData(teamData);
      } catch (err) {
        setError('Failed to load team summary');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-text-secondary">Loading team summary...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-danger text-lg">{error || 'No data available'}</p>
        </div>
      </div>
    );
  }

  // Group reps by department type
  const contentServicesDepts = ['Content Services', 'Territory Sales'];
  const saasReps = data.teamMetrics.filter((rep) => !contentServicesDepts.includes(rep.department));
  const csReps = data.teamMetrics.filter((rep) => contentServicesDepts.includes(rep.department));

  // Calculate department totals
  const saasTotals = {
    pipeline: saasReps.reduce((sum, r) => sum + r.pipeline.total, 0),
    target: saasReps.reduce((sum, r) => sum + r.target, 0),
    deals: saasReps.reduce((sum, r) => sum + r.dealCounts.total, 0),
  };

  const csTotals = {
    pipeline: csReps.reduce((sum, r) => sum + r.pipeline.total, 0),
    target: csReps.reduce((sum, r) => sum + r.target, 0),
    deals: csReps.reduce((sum, r) => sum + r.dealCounts.total, 0),
  };

  return (
    <div className="bg-background text-text-primary">
      {/* Header */}
      <header className="bg-surface border-b border-border">
        <div className="max-w-full mx-auto px-12 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-primary">Team Performance</h1>
              <p className="text-text-secondary mt-1">
                Path to All Targets: March 2026 ({formatCompactCurrency(data.config.march2026Target)}) |
                FY26 ({formatCompactCurrency(data.config.fy26Target)}) |
                FY27 ({formatCompactCurrency(data.config.fy27Target)})
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-text-muted">Team Achievement</p>
              <p className={`text-2xl font-bold ${data.teamTotals.onTrack ? 'text-success' : 'text-warning'}`}>
                {data.teamTotals.teamAchievement.toFixed(0)}%
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-full mx-auto px-12 py-8">
        {/* Team Overview */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-text-primary mb-4">Team Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <MetricCard
              title="Total Pipeline"
              value={formatCompactCurrency(data.teamTotals.totalPipeline)}
              subtitle={`${data.teamTotals.totalDeals} deals`}
              color="primary"
            />
            <MetricCard
              title="Combined Target"
              value={formatCompactCurrency(data.teamTotals.totalTarget)}
              subtitle={`${data.teamMetrics.length} team members`}
              color="warning"
            />
            <MetricCard
              title="High Confidence"
              value={formatCompactCurrency(data.teamTotals.highConfidencePipeline)}
              subtitle={`${data.teamTotals.highConfidenceDeals} deals`}
              color="success"
            />
            <MetricCard
              title="Pipeline Coverage"
              value={`${((data.teamTotals.totalPipeline / data.teamTotals.totalTarget) * 100).toFixed(0)}%`}
              subtitle={data.teamTotals.onTrack ? 'On Track' : 'Below Target'}
              color={data.teamTotals.onTrack ? 'success' : 'warning'}
            />
          </div>
        </section>

        {/* Team Confidence Breakdown */}
        <section className="mb-8">
          <Card>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-text-primary">Team Pipeline by Confidence</h3>
                <p className="text-sm text-text-muted">Distribution across all team members</p>
              </div>
              <div className="flex gap-8">
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-400">
                    {formatCompactCurrency(data.teamTotals.highConfidencePipeline)}
                  </p>
                  <p className="text-xs text-text-muted">HIGH ({data.teamTotals.highConfidenceDeals})</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-yellow-400">
                    {formatCompactCurrency(data.teamTotals.mediumConfidencePipeline)}
                  </p>
                  <p className="text-xs text-text-muted">MEDIUM ({data.teamTotals.mediumConfidenceDeals})</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-400">
                    {formatCompactCurrency(data.teamTotals.lowConfidencePipeline)}
                  </p>
                  <p className="text-xs text-text-muted">LOW ({data.teamTotals.lowConfidenceDeals})</p>
                </div>
              </div>
            </div>
          </Card>
        </section>

        {/* SaaS Sales Team */}
        {saasReps.length > 0 && (
          <section className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-semibold text-text-primary flex items-center gap-2">
                  <span>☁️</span> SaaS Sales Team
                </h2>
                <p className="text-sm text-text-muted">
                  {saasReps.length} reps | {formatCompactCurrency(saasTotals.pipeline)} pipeline | {saasTotals.deals} deals
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-text-muted">Department Coverage</p>
                <p className={`text-lg font-bold ${
                  saasTotals.target > 0 && (saasTotals.pipeline / saasTotals.target) >= 0.8
                    ? 'text-green-400'
                    : 'text-orange-400'
                }`}>
                  {saasTotals.target > 0 ? ((saasTotals.pipeline / saasTotals.target) * 100).toFixed(0) : 0}%
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {saasReps.map((rep) => (
                <RepCard
                  key={rep.id}
                  rep={rep}
                  onClick={() => navigate(`/individual/${rep.id}`)}
                />
              ))}
            </div>
          </section>
        )}

        {/* Content Services Team */}
        {csReps.length > 0 && (
          <section className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-semibold text-text-primary flex items-center gap-2">
                  <span>📄</span> Content Services Team
                </h2>
                <p className="text-sm text-text-muted">
                  {csReps.length} reps | {formatCompactCurrency(csTotals.pipeline)} pipeline | {csTotals.deals} deals
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-text-muted">Department Coverage</p>
                <p className={`text-lg font-bold ${
                  csTotals.target > 0 && (csTotals.pipeline / csTotals.target) >= 0.8
                    ? 'text-green-400'
                    : 'text-orange-400'
                }`}>
                  {csTotals.target > 0 ? ((csTotals.pipeline / csTotals.target) * 100).toFixed(0) : 0}%
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {csReps.map((rep) => (
                <RepCard
                  key={rep.id}
                  rep={rep}
                  onClick={() => navigate(`/individual/${rep.id}`)}
                />
              ))}
            </div>
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className="mt-12 border-t border-border bg-surface">
        <div className="max-w-full mx-auto px-12 py-4">
          <p className="text-center text-sm text-text-muted">
            {data.config.companyName} - Sales Dashboard © 2025
          </p>
        </div>
      </footer>
    </div>
  );
};
